/**
 * @copyright Copyright 2020 Epic Systems Corporation
 * @file Main Component
 * @author Dustin Gage
 */

declare let __webpack_public_path__: string;
declare let __react_scripts_folder__: string;

import React, { memo, useState, useCallback, ReactElement } from "react";
import { render } from "react-dom";
import { hot } from "react-hot-loader";
import ReactGA from "react-ga";

//providers
import { __SettingsContext } from "../api/use-settings";
import { __SelfTriageContext } from "../api/use-self-triage";

import { ErrorBoundary } from "./ErrorBoundary";
import { Layout } from "./layout/Layout";
import { SiteSettings, HostSettings, ISelfTriage } from "../types";

const host: HostSettings = (global as any).$$HostSettings; //from host
if (host) {
	__webpack_public_path__ = `${host.websiteUrl}${__react_scripts_folder__}`;
}

//pulled in from /custom set up for each customer
const settings: SiteSettings = (global as any).$$SiteSettings; //settings.js
const selfTriage: ISelfTriage = (global as any).$$SelfTriage; //triage.js

const App = memo(function App(): ReactElement {
	const { sitePath, locales } = settings;

	const fullPath = host ? `${host.websiteUrl}${host.sitePath}` : `/${sitePath}`;
	const errorPath = host ? `${host.websiteUrl}Home/Error?Code=1` : "";

	const [state, setState] = useState<SiteSettings>({
		...settings,
		sitePath: fullPath,
		locales: locales ? locales : { ["en-US"]: "English" },
		currentLocale: host ? host.hostLanguage : "en-US",
	});
	const { schedulingLocation, isHomeButtonHidden } = state;

	const setSchedulingLocation = useCallback(
		(locationId: string) => {
			setState({
				...state,
				schedulingLocation: locationId,
			});
		},
		[schedulingLocation],
	);

	const setHomeButtonIsHidden = useCallback(
		(isVisible: boolean) => {
			setState({
				...state,
				isHomeButtonHidden: isVisible,
			});
		},
		[isHomeButtonHidden],
	);

	const settingsContext: SiteSettings = {
		...state,
		setSchedulingLocation,
		setHomeButtonIsHidden,
	};

	return (
		<ErrorBoundary errorPath={errorPath} analyticsTrackingId={analyticsTrackingId}>
			<__SettingsContext.Provider value={settingsContext}>
				<__SelfTriageContext.Provider value={selfTriage}>
					<Layout />
				</__SelfTriageContext.Provider>
			</__SettingsContext.Provider>
		</ErrorBoundary>
	);
});

const { analyticsTrackingId } = settings;
if (analyticsTrackingId !== "") {
	ReactGA.initialize(analyticsTrackingId, {
		debug: __DEV_MODE__,
	});
}

const Root = hot(module)(App);
const element = document.getElementById("appRoot");

render(<Root />, element);
